import React, { useContext, useEffect, useRef, useState, Suspense, lazy } from 'react';
import { Route, Routes, useSearchParams } from 'react-router-dom';
import { ThemeProvider, Box, Typography } from '@mui/material';
import { CheckoutContext, CheckoutContextProps } from './context/CheckoutContext';
import {
  BaseUrlToCompany,
  DUBAI_FITNESS_CHALLENGE_COMPANY_ID,
  RegistrationStep,
  SPORTS_DISTRICT_COMPANY_ID,
} from './Utils/constants';
import { getCompanyPrimaryColor, getCompanyThemeOptions } from './theme/getCompanyThemeOptions';
import {
  applySportsDistrictDocumentStyles,
  resetSportsDistrictDocumentStyles,
} from './theme/applySportsDistrictDocumentStyles';
import { COLORS } from './Utils/colors';
import { createCustomTheme } from './theme';
import { useDfcPageDirection } from './theme/dfcDirection';
import { AppWrapper } from './Style';
import useCompany from './hooks/useCompany';
import useCart from './hooks/useCart';
import Layout from './components/Layout';
import ContentLoader from './components/shared/ContentLoader';
import DfcPageLoader from './components/shared/dfcVillage/PageLoader';
import AsaPageLoader from './components/shared/asa/AsaPageLoader';
import AsaRoute from './pages/asa/AsaRoute';
import AsaFlowShell from './pages/asa/AsaFlowShell';
import { ASA_PURCHASE_HEADINGS } from './Utils/asaBrand';
import { isAsaCompany, isLikelyAsaCompanyId } from './Utils/asaBrand';
import Club15PageLoader from './components/shared/club15/Club15PageLoader';
import { isClub15Company, isLikelyClub15CompanyId } from './Utils/club15Brand';
import 'react-toastify/dist/ReactToastify.css';
import HTMLHeadManipulator from './HTMLHeadManipulator';
import { subscribeToTopic, unsubscribeFromTopic } from './Utils/fcmToken';

// Lazy load all page components
const Calendar = lazy(() => import('./pages/calendar'));
const MarketplaceCalendar = lazy(() => import('./pages/MartketplaceCalendar'));
const MarketplaceClasses = lazy(() => import('./pages/MarketplaceClasses'));
const ClassCalendar = lazy(() => import('./pages/class'));
const CoachCalendar = lazy(() => import('./pages/coach'));
const GymCalendar = lazy(() => import('./pages/gym'));
const KidsAreaCalendar = lazy(() => import('./pages/kidsArea'));
const KidsActivitiesCalendar = lazy(() => import('./pages/kidsActivities'));
const PodsClasses = lazy(() => import('./pages/PodsClasses'));
const SchoolClasses = lazy(() => import('./pages/SchoolClasses'));
const SchoolExperience = lazy(() => import('./pages/SchoolExperience'));
const SchoolAgeGroupRedirect = lazy(
  () => import('./pages/SchoolExperience/SchoolAgeGroupRedirect')
);
const KidsBooking = lazy(() => import('./pages/kidsArea/KidsBooking'));
const PodsBooking = lazy(() => import('./pages/PodsClasses/PodsBooking'));
const SchoolBooking = lazy(() => import('./pages/SchoolClasses/SchoolBooking'));
const Checkout = lazy(() => import('./pages/checkout'));
const Confirmation = lazy(() => import('./pages/confirmation'));
const Cancellation = lazy(() => import('./pages/cancellation'));
const Status = lazy(() => import('./pages/status'));
const PackageConfirmation = lazy(() => import('./pages/package/packageConfirmation'));
const PackagePurchase = lazy(() => import('./pages/package/packagePurchase'));
const MembershipPurchase = lazy(() => import('./pages/package/membershipPurchase'));
const PackageCancellation = lazy(() => import('./pages/package/packageCancellation'));
const Cart = lazy(() => import('./pages/cart'));
const Profile = lazy(() => import('./pages/profile'));
const CartCheckout = lazy(() => import('./pages/cart/CartCheckout'));
const AddOns = lazy(() => import('./pages/cart/AddOns'));
const Packages = lazy(() => import('./pages/package/packages'));
const AsaOffers = lazy(() => import('./pages/asa/AsaOffers'));
const AsaPackageConfirmation = lazy(() => import('./pages/asa/AsaPackageConfirmation'));
const Register = lazy(() => import('./pages/register'));
const Home = lazy(() => import('./pages/Home'));
const ResetPassword = lazy(() => import('./pages/resetPassword'));
const FunActivities = lazy(() => import('./pages/funActivities'));
const ConsentForm = lazy(() => import('./pages/consentForm'));
const BirthdayCelebrationForm = lazy(() => import('./pages/birthdayCelebrationForm'));
const EmailVerification = lazy(() => import('./pages/emailVerification'));
const VerifyCode = lazy(() => import('./pages/emailVerification/verifyCode'));
const InValidEmailVerification = lazy(() => import('./pages/emailVerification/inValidEmailVerification'));
const EmailVerificationConfirmation = lazy(() => import('./pages/emailVerification/emailVerificationConfirmation'));
const MemberShips = lazy(() => import('./pages/memberships'));
const Privacy = lazy(() => import('./pages/Privacy'));
const CancellationPolicy = lazy(() => import('./pages/CancellationPolicy'));
const CookiesPolicy = lazy(() => import('./pages/CookiesPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const DswRaffleTerms = lazy(() => import('./pages/DswRaffleTerms'));
const LiabilityWaiver = lazy(() => import('./pages/LiabilityWaiver'));
const Error = lazy(() => import('./pages/Error'));
const GroupedMemberships = lazy(() => import('./pages/memberships/groupedMemberships'));
const GroupBookingConfirmation = lazy(() => import('./pages/group-booking'));
const ShareBooking = lazy(() => import('./pages/share-booking'));
const BannersPage = lazy(() => import('./pages/banners'));
const CustomFormPage = lazy(() => import('./pages/customForm'));
const FormConfirmation = lazy(() => import('./pages/formConfirmation'));
const IframePage = lazy(() => import('./pages/iframe'));
const ClassSessionDetail = lazy(() => import('./pages/classSessionDetail'));
const Timetable = lazy(() => import('./pages/timetable'));
const TicketedEvents = lazy(() => import('./pages/TicketedEvents'));
const FitnessVillage = lazy(() => import('./pages/FitnessVillage'));
const TicketedEventDetail = lazy(() => import('./pages/TicketedEvents/TicketedEventDetail'));

const App: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [theme, setTheme] = useState<any>(() => createCustomTheme(COLORS.blue));
  const [searchParams] = useSearchParams();

  const companyId =
    searchParams.get('companyId') || BaseUrlToCompany.get(window.location.origin) || '';
  const { loading: isLoadingCompany } = useCompany(companyId);
  const { company } = useContext(CheckoutContext) as CheckoutContextProps;
  const previousCompanyIdRef = useRef<string | null>(null);
  const { direction } = useDfcPageDirection();

  const toggleDrawer = (open: boolean) => setIsOpen(open);

  useEffect(() => {
    if (isLoadingCompany) {
      return;
    }

    const primaryColor = getCompanyPrimaryColor(company?.id, company?.primaryColor);
    setTheme(createCustomTheme(primaryColor, getCompanyThemeOptions(company?.id), direction));
  }, [isLoadingCompany, company?.primaryColor, company?.id, direction]);

  useEffect(() => {
    if (company?.id === SPORTS_DISTRICT_COMPANY_ID) {
      applySportsDistrictDocumentStyles();
      return resetSportsDistrictDocumentStyles;
    }

    resetSportsDistrictDocumentStyles();
  }, [company?.id]);

  useEffect(() => {
    const shouldSubscribe =
      company?.supportPushNotifications === true && company?.id;

    if (shouldSubscribe) {
      const topic = company!.id;
      if (previousCompanyIdRef.current && previousCompanyIdRef.current !== topic) {
        unsubscribeFromTopic(previousCompanyIdRef.current);
      }
      subscribeToTopic(topic);
      previousCompanyIdRef.current = topic;
    } else {
      if (previousCompanyIdRef.current) {
        unsubscribeFromTopic(previousCompanyIdRef.current);
        previousCompanyIdRef.current = null;
      }
    }

    return () => {
      if (previousCompanyIdRef.current) {
        unsubscribeFromTopic(previousCompanyIdRef.current);
        previousCompanyIdRef.current = null;
      }
    };
  }, [company?.id, company?.supportPushNotifications]);

  useCart(true);

  // The DFC site knows it is DFC from the URL alone, but its header, menu and page designs
  // come with the company. Until the company is fetched, show only the DFC loader — no
  // generic header, no half-built home, no banner pushing the page down when it lands.
  const isDfcSite = companyId === DUBAI_FITNESS_CHALLENGE_COMPANY_ID;
  if (isDfcSite && isLoadingCompany) {
    return (
      <ThemeProvider theme={theme}>
        <HTMLHeadManipulator />
        <DfcPageLoader fullScreen />
      </ThemeProvider>
    );
  }

  // All Sports Arena: its own loader. Before the company loads only an id hint can tell
  // (`isLikelyAsaCompanyId`); once it has loaded, its theme decides.
  const isAsaSite = isLoadingCompany ? isLikelyAsaCompanyId(companyId) : isAsaCompany(company);
  if (isAsaSite && isLoadingCompany) {
    return (
      <ThemeProvider theme={theme}>
        <HTMLHeadManipulator />
        <AsaPageLoader fullScreen />
      </ThemeProvider>
    );
  }

  // Club 15: the same, with its own loader and its own id hint (`isLikelyClub15CompanyId`).
  const isClub15Site = isLoadingCompany
    ? isLikelyClub15CompanyId(companyId)
    : isClub15Company(company);
  if (isClub15Site && isLoadingCompany) {
    return (
      <ThemeProvider theme={theme}>
        <HTMLHeadManipulator />
        <Club15PageLoader fullScreen />
      </ThemeProvider>
    );
  }

  const pageLoader = isDfcSite ? (
    <DfcPageLoader />
  ) : isAsaSite ? (
    <AsaPageLoader />
  ) : isClub15Site ? (
    <Club15PageLoader />
  ) : (
    <ContentLoader isLoading={true}>
      <div />
    </ContentLoader>
  );

  return (
    <ThemeProvider theme={theme}>
      <HTMLHeadManipulator />
      {company?.isBetaVersion && (
        <Box sx={{ backgroundColor: 'yellow', padding: '10px', textAlign: 'center' }}>
          <Typography variant="body1" sx={{ fontFamily: 'Dubai-Bold' }}>
            This is a test website. All bookings are test bookings, and will be deleted.
          </Typography>
        </Box>
      )}
      <AppWrapper>
        <Layout isOpen={isOpen} toggleDrawer={toggleDrawer}>
          <Suspense fallback={pageLoader}>
            <Routes>
              <Route path="/calendar" element={<Calendar title="Calendar" />} />
              <Route
                path="/MarketplaceCalendar"
                element={<MarketplaceCalendar title="MarketplaceCalendar" />}
              />
              <Route
                path="/MarketplaceClasses"
                element={<MarketplaceClasses title="MarketplaceClasses" />}
              />
              <Route path="/Class" element={<ClassCalendar title="Class" />} />
              <Route path="/Coach" element={<CoachCalendar title="Coach" />} />
              <Route path="/Gym" element={<GymCalendar title="Gym" />} />
              <Route path="/KidsArea" element={<KidsAreaCalendar title="KidsArea" />} />
              <Route
                path="/KidsActivities"
                element={<KidsActivitiesCalendar title="KidsActivities" />}
              />
              <Route path="/PodsClasses" element={<PodsClasses title="PodsClasses" />} />
              <Route path="/SchoolClasses" element={<SchoolClasses title="SchoolClasses" />} />
              <Route
                path="/SchoolExperience"
                element={<SchoolExperience title="School Experience" />}
              />
              <Route path="/SchoolAgeGroup" element={<SchoolAgeGroupRedirect />} />
              <Route path="/KidsBooking" element={<KidsBooking title="KidsBooking" />} />
              <Route path="/PodsBooking" element={<PodsBooking title="PodsBooking" />} />
              <Route path="/SchoolBooking" element={<SchoolBooking title="SchoolBooking" />} />
              <Route path="/Checkout" element={<Checkout title="Booking Details" />} />
              <Route path="/Confirmation" element={<Confirmation title="Confirmation" />} />
              <Route path="/Cancellation" element={<Cancellation title="Cancellation" />} />
              <Route path="/Status" element={<Status title="Status" />} />
              <Route
                path="/PackageConfirmation"
                element={
                  <AsaRoute asa={<AsaPackageConfirmation />}>
                    <PackageConfirmation title="PackageConfirmation" />
                  </AsaRoute>
                }
              />
              <Route
                path="/PackagePurchase"
                element={
                  <AsaRoute
                    asa={
                      <AsaFlowShell {...ASA_PURCHASE_HEADINGS.credit}>
                        <PackagePurchase title="Checkout" />
                      </AsaFlowShell>
                    }
                  >
                    <PackagePurchase title="PackagePurchase" />
                  </AsaRoute>
                }
              />
              <Route
                path="/MembershipPurchase"
                element={
                  <AsaRoute
                    asa={
                      <AsaFlowShell {...ASA_PURCHASE_HEADINGS.membership}>
                        <MembershipPurchase title="Checkout" />
                      </AsaFlowShell>
                    }
                  >
                    <MembershipPurchase title="MembershipPurchase" />
                  </AsaRoute>
                }
              />
              <Route
                path="/PackageCancellation"
                element={<PackageCancellation title="PackageCancellation" />}
              />
              <Route path="/Cart" element={<Cart title="Cart" />} />
              <Route path="/My-profile" element={<Profile title="My-profile" />} />
              <Route path="/CartCheckout" element={<CartCheckout title="Checkout" />} />
              <Route path="/AddOns" element={<AddOns title="Add-ons" />} />
              <Route
                path="/Packages"
                element={
                  <AsaRoute asa={<AsaOffers />}>
                    <Packages title="Packages" />
                  </AsaRoute>
                }
              />
              <Route
                path="/Register"
                element={
                  <Register
                    registrationStep={RegistrationStep.SignUp}
                    title="Register"
                    redirectToCalendar={true}
                  />
                }
              />
              <Route path="/" element={<Home title="Home" />} />
              <Route path="/ResetPassword" element={<ResetPassword title="ResetPassword" />} />
              <Route path="/Fun-activities" element={<FunActivities title="Fun-activities" />} />
              <Route path="/Consent-form" element={<ConsentForm title="Consent-form" />} />
              <Route
                path="/birthday-celebration"
                element={<BirthdayCelebrationForm title="birthday-celebration" />}
              />
              <Route path="/EmailVerification" element={<EmailVerification />} />
              <Route path="/VerifyCode" element={<VerifyCode title="Verify Code" />} />
              <Route
                path="/InValidEmailVerification"
                element={<InValidEmailVerification title="InValidEmailVerification" />}
              />
              <Route
                path="/EmailVerificationConfirmation"
                element={<EmailVerificationConfirmation title="EmailVerificationConfirmation" />}
              />
              <Route
                path="/memberships"
                element={
                  <AsaRoute asa={<AsaOffers />}>
                    <MemberShips title="memberships" />
                  </AsaRoute>
                }
              />
              <Route
                path="/groupedMemberships"
                element={<GroupedMemberships title="grouped-memberships" />}
              />
              <Route path="/Privacy" element={<Privacy title="Privacy" />} />
              <Route
                path="/Cancellation-policy"
                element={<CancellationPolicy title="Cancellation-policy" />}
              />
              <Route
                path="/Cookies-policy"
                element={<CookiesPolicy title="Cookies-policy" />}
              />
              <Route path="/Terms" element={<Terms title="Terms" />} />
              <Route path="/raffle" element={<DswRaffleTerms title="DSW Raffle" />} />
              <Route path="/LiabilityWaiver" element={<LiabilityWaiver title="Liability Waiver" />} />
              <Route path="/GroupBookingConfirmation" element={<GroupBookingConfirmation />} />
              <Route path="/share-booking" element={<ShareBooking />} />
              <Route path="/offers" element={<BannersPage />} />
              <Route path="/forms" element={<CustomFormPage />} />
              <Route path="/form-response/:responseId" element={<FormConfirmation />} />
              <Route path="/iframe" element={<IframePage title="External Form" />} />
              <Route path="/class-session" element={<ClassSessionDetail title="Class Session Details" />} />
              <Route path="/timetable" element={<Timetable title="Today's Activities & Classes" />} />
              <Route path="/TicketedEvents" element={<TicketedEvents title="Ticketed Events" />} />
              <Route path="/TicketedEventDetail" element={<TicketedEventDetail title="Event Details" />} />
              <Route path="/FitnessVillage" element={<FitnessVillage title="Fitness Village" />} />
              <Route path="*" element={<Error />} />
            </Routes>
          </Suspense>
        </Layout>
      </AppWrapper>
    </ThemeProvider>
  );
};

export default App;
